import React, { useEffect, useMemo, useCallback } from 'react';

import {
  ModelType,
  SettingEvent,
  EventTimeSlot,
} from '../../../../../interfaces/merchants/resources/books';
import { EventTimeSlot as InputsEventTimeSlot } from '../../../../../interfaces/merchants/resources/books/inputs';

import {
  addToWaitingList,
  setDateTimePicker,
} from '../../../../../redux/actions/merchants/resources/books';
import {
  inputBookingEventTimeSlot,
  RestartReservationError,
} from '../../../../../redux/actions/merchants/resources/books/inputs';

import { default as Calendar } from '../InputBookingEventDateTimeCalendar';
import { default as List } from '../../../../../molecules/merchant/resources/book/InputBookingEventDateTimeList';

import { scrollToTop } from '../../../../../hooks/useScrollToTop';
import { useShallowEqualSelector } from '../../../../../hooks/redux';
import { ReduxState } from '../../../../../redux/reducers';
import { getMerchantResourcesBooksInputs } from '../../../../../redux/selectors/merchants/resources/books/inputs';
import { useAppDispatch } from '../../../../../redux';
import { useUserMe } from '../../../../../hooks/api/me';

interface ElementProps {
  modelType: ModelType;
  settings: SettingEvent;
  postable: boolean;
  onSubmit: () => void;
  onRestart: () => void;
  renew?: boolean;
  withExpressFlow: boolean;
  merchantPublicId: string;
  resourcePublicId: string;
}

type Props = ElementProps;

interface SetDateTimeQuery {
  start: Date;
  end: Date;
}

const InputBookingEventDateTime: React.FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const { isLoggedIn } = useUserMe();

  const useSelector = () =>
    useShallowEqualSelector((state: ReduxState) => ({
      inputs: getMerchantResourcesBooksInputs(
        state.merchantsResourcesBooksInputs,
      ),
    }));
  const { inputs } = useSelector();

  const {
    renew,
    settings,
    postable,
    withExpressFlow,
    merchantPublicId,
    resourcePublicId,
  } = props;

  const eventTimeSlot = useMemo(() => {
    return renew ? undefined : inputs.eventTimeSlot;
  }, [renew, inputs]);

  useEffect(() => {
    scrollToTop();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetDateTime = useCallback(
    async (query?: SetDateTimeQuery) => {
      const { merchantId } = inputs;
      const start = !!query ? query.start : null;
      const end = !!query ? query.end : null;
      await dispatch(
        setDateTimePicker({
          merchantId: merchantId!,
          resourceId: resourcePublicId,
          start,
          end,
        }),
      );
    },
    [inputs, dispatch, resourcePublicId],
  );

  const handleAddToWaitingList = useCallback(
    async (
      timeSlot: EventTimeSlot | undefined,
      lastName?: string,
      firstName?: string,
      email?: string,
      onReturn?: () => void,
    ) => {
      try {
        await dispatch(
          addToWaitingList(
            merchantPublicId,
            resourcePublicId,
            timeSlot!,
            lastName,
            firstName,
            email,
          ),
        );
        if (onReturn) {
          onReturn();
        }
      } catch (e) {
        // failed addToWaitingList.
      }
    },
    [dispatch, resourcePublicId, merchantPublicId],
  );

  const handleSubmit = useCallback(
    async (timeSlot: InputsEventTimeSlot) => {
      const { modelType, onSubmit, onRestart, withExpressFlow } = props;
      try {
        await dispatch(
          inputBookingEventTimeSlot(
            modelType,
            inputs.merchantId!,
            resourcePublicId,
            timeSlot,
            withExpressFlow,
            inputs.reservationId,
          ),
        );
        onSubmit();
      } catch (e) {
        if (e instanceof RestartReservationError) {
          onRestart();
        }
      }
    },
    [dispatch, props, inputs, resourcePublicId],
  );

  return (
    <>
      {settings.selectSlotsType === 'list' ? (
        <List
          loggedIn={isLoggedIn}
          settings={settings}
          postable={postable}
          onSetDateTimePicker={handleSetDateTime}
          onAddToWaitingList={handleAddToWaitingList}
          onSubmit={handleSubmit}
          withExpressFlow={withExpressFlow}
        />
      ) : (
        <Calendar
          loggedIn={isLoggedIn}
          initialEventTimeSlot={eventTimeSlot}
          settings={settings}
          postable={postable}
          onSetDateTimePicker={handleSetDateTime}
          onAddToWaitingList={handleAddToWaitingList}
          onSubmit={handleSubmit}
          withExpressFlow={withExpressFlow}
        />
      )}
    </>
  );
};

export default InputBookingEventDateTime;
